import React, { useState, useMemo } from "react";
import {
  Typography,
  Input,
  Tag,
  Space,
  Button,
  Card,
  Modal,
  Form,
  notification,
  Dropdown,
  Switch,
  Result,
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  StarFilled,
  MoreOutlined,
  FileTextOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import CaseTable from "./CaseTable";
import CourseEditDrawer from "./CourseEditDrawer";
import CaseFormModal from "./CaseFormModal";
import PersonChip from "./PersonChip";
import {
  getCourseProfessors,
  isCourseOwner,
  isCourseEditor,
  isCourseMember,
} from "../data";
import { useAppContext } from "../context/AppContext";
import * as coursesApi from "../api/courses";
import * as casesApi from "../api/cases";
import * as favoritesApi from "../api/favorites";

const { Title, Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const CourseDetailPage = ({
  professorName,
  courseList,
  setCourseList,
  allCases,
  setAllCases,
  favoriteCourseIds,
  setFavoriteCourseIds,
}) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { userRole, user } = useAppContext();
  const isProfessor = userRole === "Professeur";

  const course = courseList.find((c) => c.id === courseId);

  const [searchText, setSearchText] = useState("");
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [form] = Form.useForm();

  const isOwner = isCourseOwner(course, user);
  const isEditor = isCourseEditor(course, user);
  const isMember = isCourseMember(course, user);

  const isFavorite = course ? favoriteCourseIds.includes(course.id) : false;

  const courseCases = useMemo(() => {
    if (!course) return [];
    return allCases
      .filter((c) => c.course === course.id)
      .filter((c) => {
        if (!searchText.trim()) return true;
        const q = searchText.toLowerCase();
        return (
          c.pathology.toLowerCase().includes(q) ||
          c.region.toLowerCase().includes(q)
        );
      });
  }, [allCases, course, searchText]);

  if (!course) {
    return (
      <Result
        status="404"
        title="Cours introuvable"
        subTitle="Ce cours n'existe pas ou n'est plus accessible."
        extra={
          <Button type="primary" onClick={() => navigate("/cours")}>
            Retour aux cours
          </Button>
        }
      />
    );
  }

  // Étudiant : pas accès aux brouillons
  if (course.status === "draft" && !isProfessor) {
    return (
      <Result
        status="403"
        title="Cours non disponible"
        subTitle="Ce cours est actuellement en cours de préparation."
        extra={
          <Button type="primary" onClick={() => navigate("/cours")}>
            Retour aux cours
          </Button>
        }
      />
    );
  }

  // Prof non membre sur un brouillon : pas accès non plus
  if (course.status === "draft" && isProfessor && !isMember) {
    return (
      <Result
        status="403"
        title="Cours en brouillon"
        subTitle="Ce cours est en préparation par son propriétaire et n'est pas encore publié."
        extra={
          <Button type="primary" onClick={() => navigate("/cours")}>
            Retour aux cours
          </Button>
        }
      />
    );
  }

  const toggleFavorite = () => {
    const isFav = favoriteCourseIds.includes(course.id);
    setFavoriteCourseIds((prev) =>
      isFav ? prev.filter((id) => id !== course.id) : [...prev, course.id]
    );
    const call = isFav
      ? favoritesApi.removeCourseFavorite(course.id)
      : favoritesApi.addCourseFavorite(course.id);
    call.catch(() => {
      setFavoriteCourseIds((prev) =>
        isFav ? [...prev, course.id] : prev.filter((id) => id !== course.id)
      );
      notification.error({ message: "Impossible de mettre à jour les favoris" });
    });
  };

  const handleSaveCourse = async (values) => {
    try {
      const updated = await coursesApi.updateCourse(course.id, {
        name: values.name,
        description: values.description,
        status: isOwner ? values.status : course.status,
        ...(isOwner ? { editorIds: values.editorIds } : {}),
      });
      setCourseList((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setEditDrawerOpen(false);
      notification.success({ message: "Cours mis à jour" });
    } catch (e) {
      notification.error({ message: "Échec", description: e.message });
    }
  };

  const handleDeleteCourse = () => {
    confirm({
      title: `Supprimer définitivement "${course.name}" ?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "Cette action est irréversible. Les cas associés ne seront pas supprimés mais seront détachés du cours.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await coursesApi.deleteCourse(course.id);
          setCourseList((prev) => prev.filter((c) => c.id !== course.id));
          notification.success({ message: "Cours supprimé" });
          navigate("/cours");
        } catch (e) {
          notification.error({ message: "Échec", description: e.message });
        }
      },
    });
  };

  const handlePublishToggle = async () => {
    const next = course.status === "draft" ? "published" : "draft";
    try {
      const updated = await coursesApi.setCourseStatus(course.id, next);
      setCourseList((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      notification.success({
        message: next === "published" ? "Cours publié" : "Cours repassé en brouillon",
      });
    } catch (e) {
      notification.error({ message: "Échec", description: e.message });
    }
  };

  const handleLeaveCourse = () => {
    confirm({
      title: `Quitter le cours "${course.name}" ?`,
      icon: <ExclamationCircleOutlined />,
      content: "Vous ne pourrez plus l'éditer.",
      okText: "Quitter",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await coursesApi.removeEditor(course.id, user.id);
          setCourseList((prev) =>
            prev.map((c) =>
              c.id === course.id
                ? { ...c, editors: c.editors.filter((e) => e !== professorName) }
                : c
            )
          );
          notification.success({ message: "Vous avez quitté le cours" });
          navigate("/cours");
        } catch (e) {
          notification.error({ message: "Échec", description: e.message });
        }
      },
    });
  };

  const handleAddCase = () => {
    setEditingCase(null);
    form.resetFields();
    setCaseModalOpen(true);
  };

  const handleEditCase = (cas) => {
    if (cas.ownerId !== user.id && !isOwner) {
      // Co-éditeur tentant de modifier le cas d'un autre : confirmation forte
      confirm({
        title: "Modifier le cas d'un autre professeur ?",
        icon: <ExclamationCircleOutlined />,
        content: `Ce cas appartient à ${cas.owner}. Êtes-vous sûr de vouloir le modifier ?`,
        okText: "Continuer",
        okType: "danger",
        cancelText: "Annuler",
        onOk: () => {
          setEditingCase(cas);
          form.setFieldsValue({
            pathology: cas.pathology,
            organ: cas.organ,
            region: cas.region,
            clinicalSummary: cas.clinicalSummary,
          });
          setCaseModalOpen(true);
        },
      });
      return;
    }
    setEditingCase(cas);
    form.setFieldsValue({
      pathology: cas.pathology,
      organ: cas.organ,
      region: cas.region,
      clinicalSummary: cas.clinicalSummary,
    });
    setCaseModalOpen(true);
  };

  const pollAndRefresh = (key, jobIds) => {
    if (!jobIds || !jobIds.length) return;
    notification.info({ message: "Traitement des images en cours…" });
    casesApi.pollJobs(jobIds).then(async ({ failed }) => {
      const fresh = await casesApi.getCase(key);
      setAllCases((prev) => prev.map((c) => (c.key === key ? fresh : c)));
      notification[failed.length ? "warning" : "success"]({
        message: failed.length
          ? "Certaines images n'ont pas pu être traitées"
          : "Images prêtes",
      });
    });
  };

  const handleRemoveImage = async (assetId) => {
    if (!editingCase) return;
    try {
      await casesApi.detachImage(editingCase.key, assetId);
      const fresh = await casesApi.getCase(editingCase.key);
      setEditingCase(fresh);
      setAllCases((prev) => prev.map((c) => (c.key === fresh.key ? fresh : c)));
      notification.success({ message: "Image retirée" });
    } catch (e) {
      notification.error({ message: "Échec", description: e.message });
    }
  };

  const handleSubmitCase = async (values) => {
    try {
      if (editingCase) {
        const { case: updated, jobIds } = await casesApi.updateCaseWithImages(
          editingCase.key,
          { ...values, course: editingCase.course }
        );
        setAllCases((prev) =>
          prev.map((c) => (c.key === editingCase.key ? updated : c))
        );
        notification.success({ message: "Cas mis à jour" });
        pollAndRefresh(editingCase.key, jobIds);
      } else {
        const { case: created, jobIds } = await casesApi.createCaseWithImages({
          ...values,
          course: course.id,
        });
        setAllCases((prev) => [...prev, created]);
        notification.success({ message: "Cas ajouté" });
        pollAndRefresh(created.key, jobIds);
      }
      setCaseModalOpen(false);
      setEditingCase(null);
    } catch (e) {
      notification.error({ message: "Échec", description: e.message });
    }
  };

  const handleDeleteCase = (cas) => {
    const proceed = async () => {
      try {
        await casesApi.deleteCase(cas.key);
        setAllCases((prev) => prev.filter((c) => c.key !== cas.key));
        notification.success({ message: "Cas supprimé" });
      } catch (e) {
        notification.error({ message: "Échec", description: e.message });
      }
    };
    if (cas.ownerId !== user.id) {
      confirm({
        title: "Supprimer le cas d'un autre professeur ?",
        icon: <ExclamationCircleOutlined />,
        content: `Ce cas appartient à ${cas.owner}. Êtes-vous sûr de vouloir le supprimer ?`,
        okText: "Supprimer",
        okType: "danger",
        cancelText: "Annuler",
        onOk: proceed,
      });
    } else {
      proceed();
    }
  };

  const buildCourseActionsMenu = () => {
    const items = [{ key: "edit", label: "Modifier le cours", icon: <EditOutlined /> }];
    if (isOwner) {
      items.push({
        key: "publish",
        label: course.status === "draft" ? "Publier le cours" : "Repasser en brouillon",
      });
      items.push({ type: "divider" });
      items.push({
        key: "delete",
        label: "Supprimer le cours",
        danger: true,
        icon: <DeleteOutlined />,
      });
    } else if (isEditor) {
      items.push({ type: "divider" });
      items.push({ key: "leave", label: "Quitter le cours", danger: true });
    }
    return {
      items,
      onClick: ({ key }) => {
        if (key === "edit") setEditDrawerOpen(true);
        else if (key === "publish") handlePublishToggle();
        else if (key === "leave") handleLeaveCourse();
        else if (key === "delete") handleDeleteCourse();
      },
    };
  };

  const studentColumns = [
    {
      title: "Pathologie",
      dataIndex: "pathology",
      key: "pathology",
      render: (val) => (quizMode ? <Tag>?</Tag> : val),
      sorter: (a, b) => a.pathology.localeCompare(b.pathology),
    },
    {
      title: "Région",
      dataIndex: "region",
      key: "region",
      sorter: (a, b) => a.region.localeCompare(b.region),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Propriétaire",
      dataIndex: "owner",
      key: "owner",
      render: (owner) => <PersonChip name={owner} />,
    },
  ];

  const profColumns = [
    ...studentColumns.slice(0, 4).map((col) =>
      col.key === "pathology" ? { ...col, render: undefined } : col
    ),
    {
      key: "actions",
      align: "center",
      width: 56,
      render: (_, cas) => (
        <Dropdown
          menu={{
            items: [
              { key: "edit", label: "Modifier", icon: <EditOutlined /> },
              {
                key: "delete",
                label: "Supprimer",
                danger: true,
                icon: <DeleteOutlined />,
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") handleEditCase(cas);
              else if (key === "delete") handleDeleteCase(cas);
            },
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const columns = isMember ? profColumns : studentColumns;

  return (
    <div
      style={{
        padding: 20,
        background: "var(--color-bg)",
        color: "var(--color-text)",
        minHeight: "100vh",
      }}
    >
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/cours")}
        style={{ paddingLeft: 0, marginBottom: 12 }}
      >
        Cours
      </Button>

      <div
        style={{
          paddingBottom: 20,
          marginBottom: 24,
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <Space align="center" wrap size={8}>
              <Title
                level={2}
                style={{
                  margin: 0,
                  color: "var(--color-text)",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                {course.name}
              </Title>
              {course.status === "draft" && (
                <Tag
                  color="orange"
                  icon={<FileTextOutlined />}
                  style={{ borderRadius: 2, margin: 0 }}
                >
                  Brouillon
                </Tag>
              )}
            </Space>
            {course.description && (
              <Text
                type="secondary"
                style={{ display: "block", marginTop: 6, fontSize: 14 }}
              >
                {course.description}
              </Text>
            )}
            <div
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                color: "var(--color-text-secondary)",
                fontSize: 13,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Propriétaire
                </Text>
                <PersonChip name={course.owner} />
              </span>
              {course.editors && course.editors.length > 0 && (
                <>
                  <span style={{ color: "var(--color-border)" }}>•</span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Co-auteurs
                    </Text>
                    <Space size={[8, 4]} wrap>
                      {course.editors.map((p) => (
                        <PersonChip key={p} name={p} />
                      ))}
                    </Space>
                  </span>
                </>
              )}
              <span style={{ color: "var(--color-border)" }}>•</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <ScanOutlined />
                {courseCases.length} cas
              </span>
              {course.updatedAt && (
                <>
                  <span style={{ color: "var(--color-border)" }}>•</span>
                  <span>Mis à jour le {course.updatedAt}</span>
                </>
              )}
            </div>
          </div>

          <Space>
            <Button
              type={isFavorite ? "primary" : "default"}
              icon={isFavorite ? <StarFilled /> : <StarOutlined />}
              onClick={toggleFavorite}
              style={{ borderRadius: 2 }}
            >
              {isFavorite ? "Favori" : "Ajouter aux favoris"}
            </Button>

            {!isProfessor && (
              <Space>
                <Text>Mode quiz</Text>
                <Switch
                  checked={quizMode}
                  onChange={setQuizMode}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              </Space>
            )}

            {isMember && (
              <Dropdown menu={buildCourseActionsMenu()} trigger={["click"]}>
                <Button icon={<MoreOutlined />} style={{ borderRadius: 2 }}>
                  Actions
                </Button>
              </Dropdown>
            )}
          </Space>
        </div>
      </div>

      <Card
        style={{
          background: "var(--color-bg)",
          color: "var(--color-text)",
        }}
      >
        <Search
          placeholder="Rechercher une pathologie ou une région..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16, width: "100%" }}
          allowClear
        />

        <CaseTable dataSource={courseCases} columns={columns} />

        {isMember && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginTop: 16 }}
            onClick={handleAddCase}
          >
            Ajouter un cas
          </Button>
        )}
      </Card>

      <CourseEditDrawer
        open={editDrawerOpen}
        course={course}
        professorName={professorName}
        courseList={courseList}
        onClose={() => setEditDrawerOpen(false)}
        onSubmit={handleSaveCourse}
        onDelete={handleDeleteCourse}
      />

      <CaseFormModal
        open={caseModalOpen}
        onCancel={() => {
          setCaseModalOpen(false);
          setEditingCase(null);
        }}
        onFinish={handleSubmitCase}
        form={form}
        isEditing={!!editingCase}
        existingImages={editingCase?.images || []}
        onRemoveImage={handleRemoveImage}
      />
    </div>
  );
};

export default CourseDetailPage;
